/**
 * Unzip Helper
 * This script provides functions to unzip files from a File object or a URL.
 * It relies on the JSZip library, which must be loaded beforehand.
 */

import { numberingFdi } from "../../helpers/teethNumbering";

const JSZip = require("jszip");

export const UnzipHelper = {
  /**
   * Unzips a file from a local File object.
   * @param {File} file - The file object from an <input type="file">.
   * @returns {Promise<string[]>} A promise that resolves with an array of file names.
   */
  unzipFromFile: function (file) {
    return new Promise((resolve, reject) => {
      // Check if the file is a zip file

      if (!file || !file.name.endsWith(".zip")) {
        return reject(
          new Error("Invalid file type. Please select a .zip file."),
        );
      }

      const reader = new FileReader();
      reader.onload = event => {
        // Once the file is read, process its content
        this._processZipData(event.target.result).then(resolve).catch(reject);
      };

      reader.onerror = () => {
        reject(new Error("An error occurred while reading the file."));
      };

      // Read the file as an ArrayBuffer

      reader.readAsArrayBuffer(file);
    });
  },

  /**
   * Fetches a zip file from a URL and unzips it.
   * @param {string} url - The URL of the .zip file.
   * @returns {Promise<string[]>} A promise that resolves with an array of file names.
   */
  unzipFromUrl: function (url) {
    return new Promise(async (resolve, reject) => {
      if (!url /* || !url.endsWith(".zip") */) {
        return reject(new Error("Please provide a valid URL ending in .zip"));
      }

      try {
        // Fetch the file from the provided URL
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        // return;
        // Get the response data as an ArrayBuffer

        const data = await response.arrayBuffer();

        // Process the zip data
        const fileNames = await this._processZipData(data);

        resolve(fileNames);
      } catch (error) {
        resolve(false);
        /* new Error(
            `Failed to fetch file. Note: The server must have CORS enabled. Details: ${error.message}`,
          ) */
      }
    });
  },

  /**
   * Private helper function to process the zip data using JSZip.
   * @param {ArrayBuffer} data - The zip file content as an ArrayBuffer.
   * @returns {Promise<string[]>} A promise that resolves with an array of file names.
   * @private
   */
  _processZipData: function (data) {
    return new Promise(async (resolve, reject) => {
      // Check if JSZip is available
      if (typeof JSZip === "undefined") {
        return reject(new Error("JSZip library is not loaded."));
      }
      try {
        const zip = await JSZip.loadAsync(data);

        const fileContents = await Promise.all(
          Object.entries(zip.files)
            .filter(([filename]) =>
              /Fused Tooth|Crown |Mandible|Skull/.test(filename),
            )
            .map(async ([filename, zipEntry]) => {
              const buffer = await zipEntry.async("arraybuffer");
              let name;
              if (/Mandible|Skull/.test(filename)) {
                name = filename.includes("Skull") ? "skull" : "mandible";
              } else {
                name = numberingFdi[parseInt(filename.slice(-6, -4))].universal;
              }
              return {
                num: name,
                buffer: buffer.slice(0),
              };
            }),
        );

        resolve(fileContents);
      } catch (error) {
        reject(new Error(`Error processing zip file: ${error.message}`));
      }
    });
  },
};
